import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

/**
 * Hooks around /api/v1/faqs/ — the admin-only CRUD surface backing the
 * public FAQ page. The viewset is locked to Admin role, so non-admin
 * users will see a 403 from any of these.
 */

export type FAQCategory =
  | "general"
  | "climate"
  | "resources"
  | "data"
  | "account"
  | "other";

/** Mirrors apps/public/models.py FAQ.CATEGORY_CHOICES, in the same
 *  order so the React grid groups them consistently with the public
 *  page. Keep in sync if you add a new category. */
export const FAQ_CATEGORIES: { code: FAQCategory; label: string }[] = [
  { code: "general",   label: "General" },
  { code: "climate",   label: "Climate Action" },
  { code: "resources", label: "Resources & Publications" },
  { code: "data",      label: "Data & Indicators" },
  { code: "account",   label: "Account & Access" },
  { code: "other",     label: "Other" },
];

export interface FAQRow {
  id: number;
  question: string;
  answer: string;
  category: FAQCategory;
  category_display: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const KEY = ["faqs"];

// ---------- READ ----------

export function useFAQs(opts: { active?: boolean | null; category?: FAQCategory | null } = {}) {
  return useQuery<FAQRow[]>({
    queryKey: [...KEY, "list", opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.active === true)  params.set("active", "true");
      if (opts.active === false) params.set("active", "false");
      if (opts.category)         params.set("category", opts.category);
      const { data } = await api.get<FAQRow[] | { results: FAQRow[] }>(
        `/faqs/${params.toString() ? "?" + params.toString() : ""}`,
      );
      return Array.isArray(data) ? data : (data?.results ?? []);
    },
    staleTime: 60_000,
  });
}

// ---------- WRITE ----------

export interface CreateFAQInput {
  question: string;
  answer: string;
  category: FAQCategory;
  sort_order?: number;
  is_active?: boolean;
}

export function useCreateFAQ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFAQInput) => {
      const { data } = await api.post<FAQRow>("/faqs/", {
        question:   input.question,
        answer:     input.answer,
        category:   input.category,
        sort_order: input.sort_order ?? 0,
        is_active:  input.is_active ?? true,
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export interface UpdateFAQInput {
  id: number;
  question?: string;
  answer?: string;
  category?: FAQCategory;
  sort_order?: number;
  is_active?: boolean;
}

export function useUpdateFAQ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...rest }: UpdateFAQInput) => {
      const { data } = await api.patch<FAQRow>(`/faqs/${id}/`, rest);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteFAQ() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/faqs/${id}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

// ---------- BULK WRITE ----------

/** PATCH the same partial onto every id. Promise.allSettled so one bad
 *  request doesn't abort the rest. Invalidates the cache once at the end. */
export function useBulkUpdateFAQs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      ids: number[];
      patch: Partial<Pick<FAQRow, "is_active" | "category">>;
    }) => {
      const results = await Promise.allSettled(
        input.ids.map((id) => api.patch(`/faqs/${id}/`, input.patch)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      return { ok, fail };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useBulkDeleteFAQs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(
        ids.map((id) => api.delete(`/faqs/${id}/`)),
      );
      const ok = results.filter((r) => r.status === "fulfilled").length;
      const fail = results.length - ok;
      return { ok, fail };
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
